/**
 * DF-I3 (lead-time half) — the earliest slot a client may book for a meeting.
 *
 * v2 shipped the date picker with `disablePast` and nothing else, so a shoot
 * could be booked fifteen minutes out. Legacy computed a real floor
 * (`PurchaseFlowOrderMeta.context.ts`) and fed it to the picker as
 * `minDateTime`, plus a `date_too_soon` error naming the floor.
 *
 * The rules are legacy's, reused rather than reimplemented — the business-day
 * arithmetic (weekend roll-forward, the Friday-afternoon cut, the prestige
 * variant) lives in `utils/time/businessDays` and is the same code the legacy
 * flow runs. Only the ladder that chooses between them is ported here.
 *
 * Ladder, in order:
 *   1. No catalogue timezone → NO floor. Legacy's own guard, for the editing
 *      category where the BE returns no timezone because no meeting is needed.
 *      Note this also swallows the lead time whenever the BE fails to resolve a
 *      timezone for a cart that DOES need one (`timezone` comes
 *      back null on dev today), so legacy has the same hole. Reproduced
 *      deliberately: inventing a floor from a guessed timezone would refuse
 *      slots the shoot's own timezone allows.
 *   2. Admin → no floor. Admins book on behalf and are trusted to know.
 *   3. A prestige product in the cart → the prestige rule (longer notice).
 *   4. A favourite creative chosen → the floor collapses to now; the client has
 *      already agreed the slot with that creative. (Legacy passes the flag into
 *      the rule rather than dropping the floor, so `disablePast` still applies —
 *      reproduced here rather than short-circuiting, to keep one behaviour.)
 *   5. Otherwise → 48 hours, rolled forward over weekends.
 *
 * Everything is rounded UP to the next quarter hour, matching the picker's
 * own 15-minute steps — an un-rounded floor would leave the first offered slot
 * silently unselectable.
 */

import moment from 'moment-timezone'

import { ProductKind } from 'constants/product'
import {
  defaultTimezone,
  getMaxDateTimeForOrder,
  getMinDateTimeForOrder,
  getMinDateTimeForOrderWithPrestigeProduct,
  roundUpToNearestTimeSegment
} from 'utils/time'

/** Cart product kinds that carry the longer prestige notice. Mirrors legacy's
 *  `PrestigeProductKinds`; kept as a local list so this module needs only the
 *  kinds it decides on. */
const PRESTIGE_KINDS: ReadonlySet<string> = new Set([
  ProductKind.PRESTIGE_GROUND_AND_DRONE_VIDEO,
  ProductKind.GROUND_PHOTO_PRESTIGE,
])

/** Does the cart hold a product that needs the longer prestige notice? */
export const kindsIncludePrestige = (kinds: ReadonlyArray<string>): boolean =>
  kinds.some((kind) => PRESTIGE_KINDS.has(kind))

export interface MeetingLeadTimeInput {
  /** Shoot-location timezone from the instruction catalogue. Absent → no floor. */
  timezone?: string
  /** `true` for an admin ordering on behalf — legacy bypasses the floor. */
  isAdmin?: boolean
  /** Product kinds the cart resolves to, for the prestige rule. */
  cartKinds?: ReadonlyArray<string>
  /** A favourite creative is selected — collapses the floor to now. */
  hasSelectedCreative?: boolean
  /** Injectable clock, so the rules are testable without freezing global time. */
  now?: moment.Moment
}

/**
 * The earliest bookable meeting date, or `undefined` when no floor applies.
 *
 * @example
 * resolveMeetingMinDateTime({ timezone: 'Europe/Zurich' })   // now + 48h, quarter-hour
 * resolveMeetingMinDateTime({ timezone: undefined })         // undefined — no floor
 * resolveMeetingMinDateTime({ timezone: 'Europe/Zurich', isAdmin: true }) // undefined
 */
export const resolveMeetingMinDateTime = ({
  timezone,
  isAdmin = false,
  cartKinds = [],
  hasSelectedCreative = false,
  now = moment(),
}: MeetingLeadTimeInput): Date | undefined => {
  if (!timezone) return undefined
  if (isAdmin) return undefined

  const raw = kindsIncludePrestige(cartKinds)
    ? getMinDateTimeForOrderWithPrestigeProduct(now, timezone)
    : getMinDateTimeForOrder(now, timezone, hasSelectedCreative)

  return roundUpToNearestTimeSegment(raw, 'quarter-hour')
}

/**
 * The floor formatted for the `date_too_soon` message, in the SHOOT's timezone —
 * the timezone the client is booking into, not their own. Same format legacy
 * used so the existing translation reads correctly.
 */
export const formatMinDateTime = (minDateTime: Date, timezone: string): string =>
  moment(minDateTime).tz(timezone).format('DD. MM. YYYY HH:mm')

/**
 * The latest bookable meeting moment, for admins too: legacy's two-year bound, which the backend's own
 * booking window is a day looser than, so a date the picker allows is never refused at placement.
 */
export const resolveMeetingMaxDateTime = (timezone?: string, now: moment.Moment = moment()): Date =>
  getMaxDateTimeForOrder(now, timezone ?? '')

/** Is the date past the latest bookable day? Compared by calendar day in the shoot's timezone, so that day stays fully bookable. */
export const isMeetingDateTooFar = (date: moment.MomentInput, maxDateTime: Date, timezone?: string): boolean => {
  const zone = timezone || defaultTimezone
  return moment(date).tz(zone).isAfter(moment(maxDateTime).tz(zone), 'day')
}

/** The bound formatted for the `date_too_far` message, in the shoot's timezone, as legacy formats it. */
export const formatMaxDateTime = (maxDateTime: Date, timezone?: string): string =>
  moment(maxDateTime).tz(timezone || defaultTimezone).format('DD. MM. YYYY')
